import { Access } from "@p0security/shared/types/access/index";

import {
  HARBOR_PRIOR_ROLES,
  HARBOR_ROLES,
  HARBOR_WAS_SYSTEM_ADMIN,
} from "./roles.ts";

export const HarborAccesses = {
  "project-role": Access({
    description: "A direct member role on a Harbor project",
    label: "Project role",
    maturity: "beta",
    delegation: {},
    generated: {
      // The user's direct role on the project when the request was staged,
      // or `none` when they had none. Revoking puts it back only when it
      // ranks below the role the user holds by then, and removes the member
      // for `none`: a revoke only ever lowers a role.
      priorRole: {
        type: "string",
        label: "Prior role",
        description: "The direct role restored when this access ends",
        choices: HARBOR_PRIOR_ROLES,
      },
    },
    // Required parameters are CLI positionals, in this order, so this reads
    // `project-role <instance> <project> <role>`.
    parameters: {
      // An installed instance, by its id or its label in P0. The backend's
      // `newHarborCommands` selects a lone one in the request forms.
      instance: {
        type: "static",
        label: "Harbor instance",
        description: "The installed Harbor instance",
      },
      // The id of the item `instance` resolved to.
      instanceId: { type: "generated", label: "Harbor instance ID" },
      // Chosen from the instance's projects, which the request forms list in
      // full. Slack shows only the first 100 of them, by name.
      project: {
        type: "static",
        label: "Project",
        description: "Harbor project name",
      },
      role: {
        type: "static",
        label: "Role",
        description: "Project role to grant",
        choices: HARBOR_ROLES,
      },
      // The grantee's Harbor user, found by their email. Request-time checks
      // see only the permission, so the user is a parameter, not generated
      // data. The id is Harbor's `user_id` as a decimal string, and means
      // something only on the instance it was found on.
      userId: { type: "generated", label: "Harbor user ID" },
      username: { type: "generated", label: "Harbor username" },
    },
    // Names the instance, so a notification says which Harbor the project is
    // on.
    render: {
      locator: "instanceId",
      policy: "role",
      resource: "project",
    } as const,
    // Harbor holds one direct role per user per project
    singleValued: { parameter: "role", per: "project" },
  }),
  "system-admin": Access({
    description:
      "Harbor's system administrator flag, which gives full control of the instance",
    label: "System administrator",
    maturity: "beta",
    delegation: {},
    generated: {
      // Whether the user had Harbor's system administrator flag when the
      // request was staged: always `false`, since stage refuses a user who has
      // it. Recorded anyway, and required: a revoke clears the flag only when
      // it's `false`, and one that can't read it clears nothing.
      wasSystemAdmin: {
        type: "string",
        label: "Was system administrator",
        description:
          "Whether the user was a Harbor system administrator before this access",
        choices: HARBOR_WAS_SYSTEM_ADMIN,
      },
    },
    // Reads `system-admin <instance>`.
    parameters: {
      // An installed instance that has an admin credential, by its id or its
      // label in P0, selected in the forms as for a project role.
      instance: {
        type: "static",
        label: "Harbor instance",
        description: "The installed Harbor instance",
      },
      // The id of the item `instance` resolved to. The access's one resource,
      // so it also stands for the permission (see the lifecycle).
      instanceId: { type: "generated", label: "Harbor instance ID" },
      // The grantee's Harbor user, found by their email, as for a project
      // role.
      userId: { type: "generated", label: "Harbor user ID" },
      username: { type: "generated", label: "Harbor username" },
    },
    // Names the instance, so a notification says which Harbor it's on.
    render: { locator: "instanceId" } as const,
  }),
};

export type HarborAccesses = typeof HarborAccesses;
export type HarborProjectRoleAccess = HarborAccesses["project-role"];
export type HarborSystemAdminAccess = HarborAccesses["system-admin"];
